import { useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/Common";
import { StatusBadge } from "@/components/Badges";
import { SimpleSelect } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { dateLabel, todayStr } from "@/lib/format";
import { useVendors } from "@/components/VendorPicker";

export default function LogTab() {
  const vendors = useVendors();
  const [f, setF] = useState({ from: "", to: "", vendor: "all" });
  const params = { date_from: f.from || undefined, date_to: f.to || undefined, vendor: f.vendor === "all" ? undefined : f.vendor };
  const { data } = useLoad(() => api.get("/po-receipts/log", { params }).then((r) => r.data), [params]);
  const csv = () => downloadCsv(`receipt-log-${todayStr()}.csv`, ["Date", "GRN no", "PO no", "Vendor", "Challan", "Invoice", "Items", "Accepted", "Rejected", "Opening", "Status", "Received by"],
    data.map((g) => [g.is_opening ? "Opening" : g.date, g.receipt_no, g.order_no, g.supplier, g.challan_no, g.invoice_no, g.items, g.accepted, g.rejected, g.is_opening ? "yes" : "", g.status, g.received_by]));
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs">From<Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="h-9 w-40" data-testid="log-from" /></label>
        <label className="flex items-center gap-1 text-xs">to<Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="h-9 w-40" data-testid="log-to" /></label>
        <SimpleSelect value={f.vendor} onChange={(v) => setF({ ...f, vendor: v })} testid="log-vendor" className="h-9 w-full sm:w-56" options={[["all", "All vendors"], ...vendors.map((v) => [v.name, v.name])]} />
        <Button size="sm" variant="outline" className="ml-auto" onClick={csv} disabled={!data?.length} data-testid="log-csv-btn"><Download className="h-4 w-4" /> CSV</Button>
      </div>
      {!data ? <Loading /> : (
        <div className="panel overflow-auto max-h-[calc(100vh-300px)]">
          <table className="data-table" data-testid="receipt-log-table">
            <thead><tr><th>Date</th><th>GRN no</th><th>PO no</th><th>Vendor</th><th>Challan / invoice</th><th>Items</th><th>Accepted</th><th>Rejected</th><th>Status</th></tr></thead>
            <tbody>{data.map((g) => (
              <tr key={g.id} data-testid={`log-row-${g.receipt_no}`} className={g.status === "cancelled" ? "opacity-60" : ""}>
                <td className="text-xs">{g.is_opening ? <span className="text-amber-600">Opening</span> : dateLabel(g.date)}</td>
                <td className="font-mono font-bold text-primary">{g.receipt_no}</td>
                <td><Link to={`/po-receipts/po/${g.po_id}`} className="font-mono hover:underline">{g.order_no}</Link></td>
                <td className="wrap-cell text-xs">{g.supplier}</td>
                <td className="text-xs">{[g.challan_no && `Ch ${g.challan_no}`, g.invoice_no && `Inv ${g.invoice_no}`].filter(Boolean).join(" · ") || "—"}</td>
                <td className="font-mono">{g.items}</td><td className="font-mono font-semibold text-emerald-600">{g.accepted}</td>
                <td className="font-mono text-red-500">{g.rejected || ""}</td><td><StatusBadge value={g.status} /></td>
              </tr>))}
              {!data.length && <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No receipts in this range</td></tr>}</tbody>
          </table>
        </div>)}
    </div>
  );
}
